import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import api from "@/lib/api";
import { toast } from "sonner";
import ManualPreview, { MANUAL_LABELS, ManualCoverPreview } from "@/components/ManualPreview";
import { renderManualContent } from "@/lib/pdfContent";
import StepEditor from "@/components/StepEditor";
import ThreeCaptureModal from "@/components/ThreeCaptureModal";
import EmbedShareModal from "@/components/EmbedShareModal";
import { ArrowLeft, Save, Download, Plus, Trash2, ArrowUp, ArrowDown, Loader2, FolderPlus, Upload, X, Box, Languages, BookText, ChevronDown, RefreshCw, RectangleVertical, RectangleHorizontal, Boxes, Share2 } from "lucide-react";

const blank = { title: "", product_name: "", intro: "", cover: null, steps: [], sections: [], warnings: "", materials: "", specifications: "", disclaimer: "", warranty: "", contact: "", translations: {}, interactive: {} };

const resolveManual = (m, lang) => {
  const t = m.translations?.[lang];
  if (lang === "nl" || !t) return m;
  return {
    ...m,
    product_name: t.product_name ?? m.product_name,
    intro: t.intro ?? m.intro,
    warnings: t.warnings ?? m.warnings,
    materials: t.materials ?? m.materials,
    specifications: t.specifications ?? m.specifications,
    disclaimer: t.disclaimer ?? m.disclaimer,
    warranty: t.warranty ?? m.warranty,
    steps: (m.steps || []).map((s, i) => ({ ...s, title: t.steps?.[i]?.title ?? s.title, description: t.steps?.[i]?.description ?? s.description })),
    sections: (m.sections || []).map((sec, si) => ({ ...sec, title: t.sections?.[si]?.title ?? sec.title, steps: (sec.steps || []).map((s, i) => ({ ...s, title: t.sections?.[si]?.steps?.[i]?.title ?? s.title, description: t.sections?.[si]?.steps?.[i]?.description ?? s.description })) })),
  };
};
const emptyStep = () => ({ title: "", description: "", image: null });

export default function ManualEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const [m, setM] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cover3d, setCover3d] = useState(false);
  const [lang, setLang] = useState("nl");
  const [translating, setTranslating] = useState(false);
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const [openGroup, setOpenGroup] = useState(null); // accordion: only one form group open at a time
  const [orientation, setOrientation] = useState("portrait");
  const [showShare, setShowShare] = useState(false);
  const toggleGroup = (k) => setOpenGroup((g) => (g === k ? null : k));

  useEffect(() => {
    api.get(`/manuals/${id}`).then((r) => {
      const data = { ...blank, ...r.data, sections: r.data.sections || [] };
      setM(data);
      const keys = new Set(data.steps.map((_, i) => `m-${i}`));
      (data.sections || []).forEach((sec, si) => (sec.steps || []).forEach((_, i) => keys.add(`s-${si}-${i}`)));
      setOpenKeys(keys);
    }).catch(() => { toast.error("Handleiding niet gevonden"); navigate("/manuals"); }).finally(() => setLoading(false));
  }, [id, navigate]);

  const isOpen = (k) => openKeys.has(k);
  const toggle = (k) => setOpenKeys((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const openKey = (k) => setOpenKeys((s) => new Set(s).add(k));

  const set = (k, v) => setM((p) => ({ ...p, [k]: v }));

  const uploadCover = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => set("cover", reader.result);
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  // main steps
  const setStep = (i, k, v) => setM((p) => ({ ...p, steps: p.steps.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addStep = () => { const idx = m.steps.length; setM((p) => ({ ...p, steps: [...p.steps, emptyStep()] })); openKey(`m-${idx}`); };
  const removeStep = (i) => setM((p) => ({ ...p, steps: p.steps.filter((_, idx) => idx !== i) }));
  const moveStep = (i, dir) => setM((p) => { const a = [...p.steps]; const j = i + dir; if (j < 0 || j >= a.length) return p; [a[i], a[j]] = [a[j], a[i]]; return { ...p, steps: a }; });

  // sections
  const addSection = () => setM((p) => ({ ...p, sections: [...(p.sections || []), { title: "", steps: [] }] }));
  const setSectionTitle = (si, v) => setM((p) => ({ ...p, sections: p.sections.map((s, idx) => (idx === si ? { ...s, title: v } : s)) }));
  const removeSection = (si) => setM((p) => ({ ...p, sections: p.sections.filter((_, idx) => idx !== si) }));
  const moveSection = (si, dir) => setM((p) => { const a = [...p.sections]; const j = si + dir; if (j < 0 || j >= a.length) return p; [a[si], a[j]] = [a[j], a[si]]; return { ...p, sections: a }; });
  const mapSectionSteps = (p, si, fn) => ({ ...p, sections: p.sections.map((s, idx) => (idx === si ? { ...s, steps: fn(s.steps || []) } : s)) });
  const setSectionStep = (si, i, k, v) => setM((p) => mapSectionSteps(p, si, (st) => st.map((s, idx) => (idx === i ? { ...s, [k]: v } : s))));
  const addSectionStep = (si) => { const idx = (m.sections[si]?.steps || []).length; setM((p) => mapSectionSteps(p, si, (st) => [...st, emptyStep()])); openKey(`s-${si}-${idx}`); };
  const removeSectionStep = (si, i) => setM((p) => mapSectionSteps(p, si, (st) => st.filter((_, idx) => idx !== i)));
  const moveSectionStep = (si, i, dir) => setM((p) => mapSectionSteps(p, si, (st) => { const a = [...st]; const j = i + dir; if (j < 0 || j >= a.length) return st; [a[i], a[j]] = [a[j], a[i]]; return a; }));

  const persist = () => {
    const { title, product_name, intro, cover, steps, sections, warnings, materials, specifications, disclaimer, warranty, contact, translations, interactive } = m;
    return api.put(`/manuals/${id}`, { title, product_name, intro, cover, steps, sections, warnings, materials, specifications, disclaimer, warranty, contact, translations, interactive });
  };

  const save = async () => {
    setSaving(true);
    try { await persist(); toast.success("Opgeslagen"); }
    catch { toast.error("Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const selectLang = async (target) => {
    if (target === "nl") { setLang("nl"); return; }
    if (m.translations?.[target]) { setLang(target); return; }
    setTranslating(true);
    try {
      await persist(); // ensure backend translates the latest saved content
      const { data } = await api.post(`/manuals/${id}/translate`, { lang: target });
      setM((p) => ({ ...p, translations: { ...(p.translations || {}), [target]: data.data } }));
      setLang(target);
      toast.success(`Vertaald naar ${MANUAL_LABELS[target].name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Vertaling mislukt"); }
    finally { setTranslating(false); }
  };

  const retranslate = async () => {
    if (lang === "nl") { toast.info("Selecteer eerst een vertaling (FR/EN/DE)"); return; }
    setTranslating(true);
    try {
      await persist();
      const { data } = await api.post(`/manuals/${id}/translate`, { lang });
      setM((p) => ({ ...p, translations: { ...(p.translations || {}), [lang]: data.data } }));
      toast.success(`Opnieuw vertaald naar ${MANUAL_LABELS[lang].name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Vertaling mislukt"); }
    finally { setTranslating(false); }
  };

  const getDims = (orient) => {
    const landscape = orient === "landscape";
    const pw = landscape ? 297 : 210, ph = landscape ? 210 : 297, barH = 10, botH = 10;
    const cTop = barH + 8, cBot = ph - botH, maxH = cBot - cTop;
    return { pw, ph, barH, botH, cTop, cBot, maxH };
  };

  const rasterizeImg = (src) => new Promise((res) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = im.naturalWidth || 1000; c.height = im.naturalHeight || 1000;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(im, 0, 0);
        res({ dataUrl: c.toDataURL("image/jpeg", 0.92), w: c.width, h: c.height });
      } catch { res(null); }
    };
    im.onerror = () => res(null);
    im.src = src;
  });

  // Cover / front page (page 1) drawn natively
  const drawCover = async (pdf, D, langCodes, labels, productName, coverSrc) => {
    pdf.setPage(1);
    const cx = D.pw / 2;
    // pre-measure name + image so the group can be vertically centered
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(34);
    const nameLines = pdf.splitTextToSize(productName || "Productnaam", D.pw - 60);
    let img = null;
    if (coverSrc) {
      try {
        const r = await rasterizeImg(coverSrc);
        if (r && r.w) {
          const maxW = D.pw - 70, maxH = Math.min(D.ph * 0.42, 150);
          let w = maxW, h = (w * r.h) / r.w;
          if (h > maxH) { h = maxH; w = (h * r.w) / r.h; }
          img = { dataUrl: r.dataUrl, w, h };
        }
      } catch (e) { console.error("cover image skipped", e); }
    }
    const guideH = 16, nameH = nameLines.length * 13, gap2 = img ? 16 : 0, imgH = img ? img.h : 0;
    const blockH = guideH + nameH + gap2 + imgH;
    const top = 44, bottom = D.ph - 58;
    let y = Math.max(top, top + ((bottom - top) - blockH) / 2);
    pdf.setTextColor(219, 150, 13); pdf.setFont("courier", "normal"); pdf.setFontSize(12);
    pdf.text((labels.guide || "").toUpperCase(), cx, y, { align: "center", charSpace: 0.6 });
    y += guideH;
    pdf.setTextColor(35, 35, 35); pdf.setFont("helvetica", "bold"); pdf.setFontSize(34);
    pdf.text(nameLines, cx, y, { align: "center" });
    y += nameH + gap2;
    if (img) pdf.addImage(img.dataUrl, "JPEG", cx - img.w / 2, y, img.w, img.h);
    // language badges (e.g. NL · FR · EN)
    const badges = langCodes.map((c) => c.toUpperCase()).join("   ·   ");
    pdf.setTextColor(219, 150, 13); pdf.setFont("courier", "bold"); pdf.setFontSize(13);
    pdf.text(badges, cx, D.ph - 42, { align: "center", charSpace: 1 });
    // brand footer
    pdf.setFont("courier", "bold"); pdf.setFontSize(18); pdf.setTextColor(35, 35, 35);
    const brand = "KANN";
    pdf.text(brand, cx, D.ph - 24, { align: "center" });
    const bw = pdf.getTextWidth(brand);
    pdf.setTextColor(219, 150, 13); pdf.text(".", cx + bw / 2 + 1.5, D.ph - 24);
  };

  // Table of Contents (page 2) drawn natively; entries = [[name, contentPageNumber]]
  const drawToc = (pdf, D, entries, tocLabel, productName) => {
    pdf.setPage(2);
    let y = 46;
    if (productName) {
      pdf.setTextColor(219, 150, 13); pdf.setFont("courier", "normal"); pdf.setFontSize(9);
      pdf.text(productName.toUpperCase(), D.pw / 2, y - 13, { align: "center" });
    }
    pdf.setTextColor(35, 35, 35); pdf.setFont("helvetica", "bold"); pdf.setFontSize(26);
    pdf.text(tocLabel, D.pw / 2, y, { align: "center" });
    y += 20;
    pdf.setDrawColor(236, 236, 232); pdf.setLineWidth(0.4);
    pdf.line(28, y, D.pw - 28, y);
    y += 16;
    pdf.setFontSize(13); pdf.setFont("helvetica", "normal");
    for (const [name, page] of entries) {
      pdf.setTextColor(35, 35, 35); pdf.text(name, 30, y);
      pdf.setTextColor(120, 120, 120); pdf.text(String(page), D.pw - 30, y, { align: "right" });
      const nameW = pdf.getTextWidth(name), pageW = pdf.getTextWidth(String(page));
      pdf.setDrawColor(205, 205, 205); pdf.setLineWidth(0.2); pdf.setLineDashPattern([0.7, 1.4], 0);
      pdf.line(30 + nameW + 3, y - 1.4, D.pw - 30 - pageW - 3, y - 1.4);
      pdf.setLineDashPattern([], 0);
      y += 13;
    }
  };

  // per-page black language bar + page numbers — content pages only (page 3+)
  const drawBars = (pdf, D, pageLang) => {
    const total = pdf.getNumberOfPages();
    const contentTotal = total - 2;
    for (let i = 3; i <= total; i++) {
      pdf.setPage(i);
      const lc = pageLang[i] || "nl";
      pdf.setFillColor(27, 27, 27); pdf.rect(0, 0, D.pw, D.barH, "F");
      pdf.setTextColor(255, 255, 255); pdf.setFont("helvetica", "bold"); pdf.setFontSize(9);
      pdf.text((MANUAL_LABELS[lc] || MANUAL_LABELS.nl).name.toUpperCase(), 10, D.barH / 2 + 1.6);
      pdf.setTextColor(219, 150, 13); pdf.text("KANN", D.pw - 10, D.barH / 2 + 1.6, { align: "right" });
      // bottom grey footer bar with centered page number
      pdf.setFillColor(244, 244, 244); pdf.rect(0, D.ph - D.botH, D.pw, D.botH, "F");
      pdf.setTextColor(120, 120, 120); pdf.setFont("helvetica", "normal"); pdf.setFontSize(9);
      pdf.text(`${i - 2} / ${contentTotal}`, D.pw / 2, D.ph - D.botH / 2 + 1.4, { align: "center" });
    }
  };

  const downloadPdf = async () => {
    setExporting(true);
    try {
      await persist();
      const D = getDims(orientation);
      const pdf = new jsPDF(orientation === "landscape" ? "l" : "p", "mm", "a4"); // page 1 = cover, page 2 = ToC
      const pageLang = {};
      pdf.addPage(); // page 2 (ToC)
      pdf.addPage(); // page 3 content start
      const startPage = pdf.getNumberOfPages();
      await renderManualContent(pdf, D, resolveManual(m, lang), MANUAL_LABELS[lang] || MANUAL_LABELS.nl, orientation);
      for (let p = startPage; p <= pdf.getNumberOfPages(); p++) pageLang[p] = lang;
      const L = MANUAL_LABELS[lang] || MANUAL_LABELS.nl;
      const resolved = resolveManual(m, lang);
      await drawCover(pdf, D, [lang], L, resolved.product_name, m.cover);
      drawToc(pdf, D, [[L.name.toUpperCase(), startPage - 2]], L.contents, resolved.product_name);
      drawBars(pdf, D, pageLang);
      const name = (m.title || m.product_name || "kann-handleiding").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      pdf.save(`${name}-${lang}.pdf`);
      toast.success("PDF gedownload");
    } catch (e) { console.error(e); toast.error("PDF genereren mislukt"); }
    finally { setExporting(false); }
  };

  const downloadBooklet = async () => {
    setExporting(true);
    try {
      await persist();
      const langs = ["nl", "fr", "en", "de"].filter((l) => l === "nl" || m.translations?.[l]);
      const D = getDims(orientation);
      const pdf = new jsPDF(orientation === "landscape" ? "l" : "p", "mm", "a4"); // page 1 = cover, page 2 = ToC
      const pageLang = {};
      const tocEntries = [];
      pdf.addPage(); // page 2 (ToC)

      for (const lc of langs) {
        pdf.addPage();
        const startPage = pdf.getNumberOfPages();
        tocEntries.push([(MANUAL_LABELS[lc] || MANUAL_LABELS.nl).name.toUpperCase(), startPage - 2]);
        await renderManualContent(pdf, D, resolveManual(m, lc), MANUAL_LABELS[lc] || MANUAL_LABELS.nl, orientation);
        const endPage = pdf.getNumberOfPages();
        for (let p = startPage; p <= endPage; p++) pageLang[p] = lc;
      }

      const L = MANUAL_LABELS[langs[0]] || MANUAL_LABELS.nl;
      await drawCover(pdf, D, langs, L, m.product_name, m.cover);
      drawToc(pdf, D, tocEntries, L.contents, m.product_name);
      drawBars(pdf, D, pageLang);
      const name = (m.title || m.product_name || "kann-handleiding").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      pdf.save(`${name}-boekje.pdf`);
      toast.success(`Boekje gedownload (${langs.length} ${langs.length === 1 ? "taal" : "talen"})`);
    } catch (e) { console.error(e); toast.error("Boekje genereren mislukt"); }
    finally { setExporting(false); }
  };


  if (loading) return <div className="min-h-screen flex items-center justify-center bg-kann-base"><Loader2 className="w-6 h-6 animate-spin text-kann-gold" /></div>;

  const inputCls = "w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-3 py-2.5 text-sm outline-none";
  const labelCls = "text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono";
  const groupCls = "border border-kann-border/40 rounded-lg overflow-hidden bg-kann-surface";
  const renderGroupHeader = (gid, title) => (
    <button onClick={() => toggleGroup(gid)} data-testid={`group-${gid}`} className="w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-kann-surface-active/50 transition-colors">
      <span className="text-sm font-semibold text-kann-text-pri">{title}</span>
      <ChevronDown className={`w-4 h-4 text-kann-text-sec transition-transform ${openGroup === gid ? "" : "-rotate-90"}`} />
    </button>
  );
  const availableLangs = ["nl", "fr", "en", "de"].filter((l) => l === "nl" || m.translations?.[l]);

  return (
    <div className="min-h-screen bg-kann-base">
      <div className="sticky top-0 z-20 bg-kann-surface/90 backdrop-blur border-b border-kann-border/30 px-3 md:px-6 py-3 flex items-center gap-2 flex-wrap">
        <button onClick={() => navigate("/manuals")} data-testid="back-btn" className="p-2 rounded-md hover:bg-kann-surface-active text-kann-text-sec"><ArrowLeft className="w-5 h-5" /></button>
        <input value={m.title} onChange={(e) => set("title", e.target.value)} data-testid="manual-title-input" className="flex-1 min-w-0 bg-transparent text-lg font-medium text-kann-text-pri outline-none" placeholder="Titel van de handleiding" />
        <div className="flex items-center gap-1 border border-kann-border/50 rounded-full p-0.5" data-testid="lang-switch">
          {translating && <Loader2 className="w-3.5 h-3.5 animate-spin text-kann-gold ml-1.5" />}
          {["nl", "fr", "en", "de"].map((lc) => (
            <button key={lc} onClick={() => selectLang(lc)} disabled={translating} data-testid={`lang-${lc}`}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase transition-colors ${lang === lc ? "bg-kann-ink text-white" : "text-kann-text-sec hover:bg-kann-surface-active"}`}>{lc}</button>
          ))}
        </div>
        <button onClick={retranslate} disabled={translating || lang === "nl"} data-testid="retranslate-btn" title="Vertaling opnieuw genereren" className="border border-kann-border/50 rounded-full p-2 text-kann-text-sec hover:bg-kann-surface-active transition-colors disabled:opacity-40">
          <RefreshCw className={`w-4 h-4 ${translating ? "animate-spin" : ""}`} />
        </button>
        <button onClick={save} disabled={saving} data-testid="save-manual-btn" className="border border-kann-border/50 rounded-full px-4 py-2 text-sm hover:bg-kann-surface-active transition-colors flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Opslaan
        </button>
        <button onClick={() => navigate(`/manuals/${id}/interactive`)} data-testid="open-interactive-btn" title="Interactieve 3D-handleiding" className="border border-kann-border/50 rounded-full px-4 py-2 text-sm hover:bg-kann-surface-active transition-colors flex items-center gap-2">
          <Boxes className="w-4 h-4 text-kann-gold" /> 3D Interactief
        </button>
        {m.interactive?.enabled && (
          <button onClick={() => setShowShare(true)} data-testid="open-share-btn" title="Insluiten / delen" className="border border-kann-border/50 rounded-full p-2 text-kann-text-sec hover:bg-kann-surface-active transition-colors">
            <Share2 className="w-4 h-4" />
          </button>
        )}
        <div className="flex items-center gap-1 border border-kann-border/50 rounded-full p-0.5" data-testid="orientation-switch" title="PDF-oriëntatie">
          <button onClick={() => setOrientation("portrait")} data-testid="orientation-portrait" title="Staand" className={`p-1.5 rounded-full transition-colors ${orientation === "portrait" ? "bg-kann-ink text-white" : "text-kann-text-sec hover:bg-kann-surface-active"}`}><RectangleVertical className="w-4 h-4" /></button>
          <button onClick={() => setOrientation("landscape")} data-testid="orientation-landscape" title="Liggend" className={`p-1.5 rounded-full transition-colors ${orientation === "landscape" ? "bg-kann-ink text-white" : "text-kann-text-sec hover:bg-kann-surface-active"}`}><RectangleHorizontal className="w-4 h-4" /></button>
        </div>
        <button onClick={downloadPdf} disabled={exporting} data-testid="download-pdf-btn" className="border border-kann-border/50 rounded-full px-4 py-2 text-sm hover:bg-kann-surface-active transition-colors flex items-center gap-2 disabled:opacity-60">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF
        </button>
        <button onClick={downloadBooklet} disabled={exporting} data-testid="download-booklet-btn" className="bg-kann-ink text-white font-semibold rounded-full px-5 py-2 text-sm hover:bg-black transition-colors flex items-center gap-2 disabled:opacity-60">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookText className="w-4 h-4" />} Boekje
        </button>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,460px)_1fr] gap-0">
        {/* Form */}
        <div className="p-6 space-y-3 border-r border-kann-border/30 overflow-y-auto" style={{ maxHeight: "calc(100vh - 61px)" }}>
          {/* Group: Voorpagina */}
          <div className={groupCls}>
            {renderGroupHeader("front", "Voorpagina")}
            {openGroup === "front" && (
              <div className="px-4 pb-5 pt-2 border-t border-kann-border/20 space-y-5">
                <div>
                  <label className={labelCls}>Cover / 3D-render</label>
                  {m.cover ? (
                    <div className="mt-2 relative">
                      <img src={m.cover} alt="cover" className="w-full h-32 object-contain rounded-md border border-kann-border/40 bg-kann-base" data-testid="cover-preview" />
                      <button onClick={() => set("cover", null)} data-testid="remove-cover-btn" className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-md transition-colors"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="mt-2 w-full border border-dashed border-kann-border/50 rounded-md py-5 text-sm text-kann-text-sec hover:border-kann-gold hover:text-kann-gold transition-colors flex items-center justify-center gap-2 cursor-pointer">
                      <Upload className="w-4 h-4" /> Cover uploaden
                      <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="cover-file-input" />
                    </label>
                  )}
                  <button onClick={() => setCover3d(true)} data-testid="cover-3d-btn" className="mt-2 w-full border border-dashed border-kann-border/50 rounded-md py-2.5 text-xs text-kann-text-sec hover:border-kann-gold hover:text-kann-gold transition-colors flex items-center justify-center gap-2">
                    <Box className="w-3.5 h-3.5" /> 3D-model → technische tekening
                  </button>
                </div>
                <div>
                  <label className={labelCls}>Productnaam</label>
                  <input value={m.product_name} onChange={(e) => set("product_name", e.target.value)} data-testid="product-name-input" className={`mt-2 ${inputCls}`} placeholder="bijv. Philips Hue Build-in Box" />
                </div>
                <div>
                  <label className={labelCls}>Korte intro</label>
                  <textarea value={m.intro} onChange={(e) => set("intro", e.target.value)} data-testid="intro-input" rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="Een korte introductie over het product en deze handleiding." />
                </div>
              </div>
            )}
          </div>

          {/* Group: Materiaal & specificaties */}
          <div className={groupCls}>
            {renderGroupHeader("material", "Materiaal & specificaties")}
            {openGroup === "material" && (
              <div className="px-4 pb-5 pt-2 border-t border-kann-border/20 space-y-5">
                <div>
                  <label className={labelCls}>Materiaal</label>
                  <textarea value={m.materials} onChange={(e) => set("materials", e.target.value)} data-testid="materials-input" rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="bijv. 100% PLA — hoogwaardig, 3D-geprint, niet-toxisch en biologisch afbreekbaar." />
                </div>
                <div>
                  <label className={labelCls}>Specificaties</label>
                  <textarea value={m.specifications} onChange={(e) => set("specifications", e.target.value)} data-testid="specifications-input" rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="bijv. Afmeting 62 x 62 mm · Schroef 3,5 x 16 mm + 3 mm spacer." />
                </div>
              </div>
            )}
          </div>

          {/* Group: Hoofdinstallatie */}
          <div className={groupCls}>
            {renderGroupHeader("steps", "Hoofdinstallatie — stappen")}
            {openGroup === "steps" && (
              <div className="px-4 pb-5 pt-2 border-t border-kann-border/20">
                <div className="flex items-center justify-end">
                  <button onClick={addStep} data-testid="add-step-btn" className="text-xs text-kann-gold hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Stap toevoegen</button>
                </div>
                <div className="mt-3 space-y-3">
                  {m.steps.map((s, i) => (
                    <StepEditor key={i} step={s} index={i} open={isOpen(`m-${i}`)} onToggle={() => toggle(`m-${i}`)}
                      onChange={(k, v) => setStep(i, k, v)} onRemove={() => removeStep(i)} onMove={(d) => moveStep(i, d)}
                      canUp={i > 0} canDown={i < m.steps.length - 1} testPrefix="step" />
                  ))}
                  {m.steps.length === 0 && <p className="text-sm text-kann-text-sec">Nog geen stappen. Klik op "Stap toevoegen".</p>}
                </div>
              </div>
            )}
          </div>

          {/* Group: Extra secties */}
          <div className={groupCls}>
            {renderGroupHeader("sections", "Extra secties")}
            {openGroup === "sections" && (
              <div className="px-4 pb-5 pt-2 border-t border-kann-border/20">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-kann-text-sec">Losstaande onderdelen, bijv. "Batterij vervangen".</p>
                  <button onClick={addSection} data-testid="add-section-btn" className="text-xs text-kann-gold hover:underline flex items-center gap-1 shrink-0"><FolderPlus className="w-3.5 h-3.5" /> Sectie</button>
                </div>
                <div className="mt-3 space-y-4">
                  {(m.sections || []).map((sec, si) => (
                    <div key={si} data-testid={`section-${si}`} className="border border-kann-border/40 rounded-lg p-3 bg-kann-base/50">
                      <div className="flex items-center gap-2 mb-3">
                        <input value={sec.title} onChange={(e) => setSectionTitle(si, e.target.value)} data-testid={`section-title-${si}`} className={`${inputCls} flex-1`} placeholder="Titel van de sectie (bijv. Batterij vervangen)" />
                        <button onClick={() => moveSection(si, -1)} disabled={si === 0} className="p-1.5 rounded hover:bg-kann-surface-active text-kann-text-sec disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                        <button onClick={() => moveSection(si, 1)} disabled={si === m.sections.length - 1} className="p-1.5 rounded hover:bg-kann-surface-active text-kann-text-sec disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                        <button onClick={() => removeSection(si)} data-testid={`remove-section-${si}`} className="p-1.5 rounded hover:bg-red-500/10 text-kann-text-sec hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="space-y-3">
                        {(sec.steps || []).map((s, i) => (
                          <StepEditor key={i} step={s} index={i} open={isOpen(`s-${si}-${i}`)} onToggle={() => toggle(`s-${si}-${i}`)}
                            onChange={(k, v) => setSectionStep(si, i, k, v)} onRemove={() => removeSectionStep(si, i)} onMove={(d) => moveSectionStep(si, i, d)}
                            canUp={i > 0} canDown={i < (sec.steps || []).length - 1} testPrefix={`section-${si}-step`} />
                        ))}
                      </div>
                      <button onClick={() => addSectionStep(si)} data-testid={`add-section-step-${si}`} className="mt-3 text-xs text-kann-gold hover:underline flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Stap toevoegen aan sectie</button>
                    </div>
                  ))}
                  {(m.sections || []).length === 0 && <p className="text-sm text-kann-text-sec">Nog geen extra secties.</p>}
                </div>
              </div>
            )}
          </div>

          {/* Group: Waarschuwingen, disclaimer & contact */}
          <div className={groupCls}>
            {renderGroupHeader("info", "Waarschuwingen, disclaimer & contact")}
            {openGroup === "info" && (
              <div className="px-4 pb-5 pt-2 border-t border-kann-border/20 space-y-5">
                <div>
                  <label className={labelCls}>Waarschuwingen &amp; tips</label>
                  <textarea value={m.warnings} onChange={(e) => set("warnings", e.target.value)} data-testid="warnings-input" rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="Belangrijke aandachtspunten of veiligheidstips." />
                </div>
                <div>
                  <label className={labelCls}>Disclaimer</label>
                  <textarea value={m.disclaimer} onChange={(e) => set("disclaimer", e.target.value)} data-testid="disclaimer-input" rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="bijv. KANN is een onafhankelijk bedrijf en niet gelieerd aan Philips." />
                </div>
                <div>
                  <label className={labelCls}>Garantie</label>
                  <textarea value={m.warranty} onChange={(e) => set("warranty", e.target.value)} data-testid="warranty-input" rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="bijv. 2 jaar garantie op materiaal- en productiefouten." />
                </div>
                <div>
                  <label className={labelCls}>Contact / support</label>
                  <textarea value={m.contact} onChange={(e) => set("contact", e.target.value)} data-testid="contact-input" rows={3} className={`mt-2 ${inputCls} resize-none`} placeholder="Contactgegevens onderaan de handleiding." />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="overflow-auto bg-kann-base p-8 flex justify-center" style={{ maxHeight: "calc(100vh - 61px)" }}>
          <div className="shrink-0 space-y-6" style={{ height: "fit-content" }}>
            <div className="shadow-xl">
              <ManualCoverPreview productName={resolveManual(m, lang).product_name} cover={m.cover} guide={(MANUAL_LABELS[lang] || MANUAL_LABELS.nl).guide} langCodes={availableLangs} />
            </div>
            <div className="shadow-xl">
              <ManualPreview ref={previewRef} manual={resolveManual(m, lang)} labels={MANUAL_LABELS[lang] || MANUAL_LABELS.nl} />
            </div>
          </div>
        </div>
      </div>
      {cover3d && <ThreeCaptureModal onClose={() => setCover3d(false)} onCapture={(url) => set("cover", url)} />}
      {showShare && <EmbedShareModal manualId={id} enabled={!!m.interactive?.enabled} slug={m.slug || ""} defaultName={m.product_name || m.title || ""} onSlug={(s) => setM((prev) => ({ ...prev, slug: s }))} onClose={() => setShowShare(false)} />}
    </div>
  );
}
